'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { clearLastActiveCookie } from '@libs/core';
import { getENV, useDecisions } from '../..';
import { getAuth0ClientId, getAuth0RedirectOrigin, persistAuth0ReturnTo, readAuth0ReturnTo, sanitizeSegmentUrl } from '../../utils';
import type { Auth0AppState } from '../../utils/getAuth0LoginRedirectOptions';
import { Auth0TokenSync } from './Auth0TokenSync';
import { AUTH0_CALLBACK_PATH, clearAuth0CallbackSearch, setPendingAuth0ReturnTo } from './pendingAuth0ReturnTo';
import { resolveAuth0Destination } from './resolveAuth0Destination';
type Auth0AppProviderProps = {
  readonly children: ReactNode;
};
const SCOPE = 'openid profile email offline_access';

/**
 * Wraps the app in `Auth0Provider` when `auth0` is on.
 * Otherwise renders children unchanged so the legacy auth flow is preserved.
 *
 * Uses a single fixed callback URL (`/auth/callback`) registered in the Auth0
 * dashboard. The originating page is stored in `appState.returnTo` so the
 * callback handler can redirect the user back after login.
 *
 * `onRedirectCallback` persists `returnTo` for cart merge (keeping a value
 * already stored at login, unless this is a new registration whose destination
 * was resolved to About You), stashes it for the callback redirect, and strips
 * OAuth `code`/`state` from the URL. `Auth0TokenSync` writes the `accessToken`
 * cookie, waits for guest-cart merge on the callback route, then
 * document-navigates so cookie-gated routes (Treats, account) do not race
 * the first landing.
 *
 * Mounted from the shared `PageLayout` so all routes that go through it
 * (ACP, sign-in, cart, etc.) get a single Auth0 session context.
 */
export function Auth0AppProvider(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getENV();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    NEXT_PUBLIC_AUTH0_AUDIENCE: audience,
    NEXT_PUBLIC_AUTH0_DOMAIN: domain
  } = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getAuth0ClientId();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clientId = t2;
  const isAuth0Enabled = useDecisions("auth0");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getAuth0RedirectOrigin();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const redirectOrigin = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (appState, user) => {
      clearLastActiveCookie();
      const returnTo = sanitizeSegmentUrl(resolveAuth0Destination(appState, user), redirectOrigin, {
        includeSearch: true
      });
      const appStateReturnTo = sanitizeSegmentUrl(appState?.returnTo ?? "/", redirectOrigin, {
        includeSearch: true
      });
      const loginReturnTo = readAuth0ReturnTo();
      const mergeReturnTo = returnTo === appStateReturnTo ? loginReturnTo || returnTo : returnTo;
      persistAuth0ReturnTo(mergeReturnTo);
      setPendingAuth0ReturnTo(returnTo);
      clearAuth0CallbackSearch();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const onRedirectCallback = t4;
  if (!isAuth0Enabled) {
    return children;
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      audience,
      redirect_uri: `${redirectOrigin}${AUTH0_CALLBACK_PATH}`,
      scope: SCOPE
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Auth0TokenSync />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children) {
    t7 = <Auth0Provider authorizationParams={t5} cacheLocation="localstorage" clientId={clientId} domain={domain} onRedirectCallback={onRedirectCallback} useRefreshTokens={true} useRefreshTokensFallback={true}>{t6}{children}</Auth0Provider>;
    $[6] = children;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}